import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, Mail, Calendar, CheckSquare, StickyNote, Activity, Loader2, Send, Trash2 } from 'lucide-react';
import { useApi } from '../lib/api';
import type { ApiResponse, CrmTimelineEntry } from '../types';

function iconFor(type: string) {
    switch (type) {
        case 'note': return StickyNote;
        case 'phonecall': return Phone;
        case 'email': return Mail;
        case 'appointment': return Calendar;
        case 'task': return CheckSquare;
        default: return Activity;
    }
}

// Dynamics renvoie createdon en UTC ; certaines réponses omettent le suffixe
// `Z`, ce que `Date` interprète alors à tort comme une heure déjà locale
// (aucune conversion n'est donc appliquée). On force l'UTC avant de laisser
// le navigateur reconvertir vers son fuseau local.
function formatDateTime(iso: string) {
    const utcIso = /[Zz]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
    return new Date(utcIso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface OpportunityTimelineProps {
    opportunityId: string;
}

// Chronologie de l'opportunité Dynamics (notes + activités CRM), affichée
// directement depuis le CRM plutôt que copiée localement : ce qu'on voit ici
// est exactement ce que montre l'onglet Chronologie de Dynamics.
export function OpportunityTimeline({ opportunityId }: OpportunityTimelineProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [note, setNote] = useState('');

    const { data, isLoading, isError } = useQuery({
        queryKey: ['opportunity-timeline', opportunityId],
        queryFn: () => api.get<ApiResponse<CrmTimelineEntry[]>>(`/api/opportunities/${opportunityId}/timeline`),
        staleTime: 60 * 1000,
    });

    const addNote = useMutation({
        mutationFn: (text: string) => api.post(`/api/opportunities/${opportunityId}/timeline`, { note: text }),
        onSuccess: () => {
            setNote('');
            queryClient.invalidateQueries({ queryKey: ['opportunity-timeline', opportunityId] });
        },
    });

    const deleteNote = useMutation({
        mutationFn: (noteId: string) => api.delete(`/api/opportunities/${opportunityId}/timeline/${noteId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunity-timeline', opportunityId] }),
    });

    const entries = data?.data || [];

    return (
        <div className="mt-2 pt-4 border-t border-[var(--color-border)]">
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3">Chronologie</h3>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (note.trim()) addNote.mutate(note.trim());
                }}
                className="flex gap-2 mb-4"
            >
                <input
                    className="input flex-1"
                    placeholder="Entrer une note..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
                <button type="submit" disabled={!note.trim() || addNote.isPending} className="btn-secondary px-3">
                    {addNote.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </form>
            {addNote.isError && (
                <p className="text-xs text-[var(--color-error)] mb-3">L'ajout de la note a échoué.</p>
            )}
            {deleteNote.isError && (
                <p className="text-xs text-[var(--color-error)] mb-3">La suppression de la note a échoué.</p>
            )}

            {isLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[var(--color-primary)]" /></div>
            ) : isError ? (
                <p className="text-sm text-[var(--color-text-muted)]">Chronologie CRM indisponible pour le moment.</p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">Aucune activité pour l'instant.</p>
            ) : (
                <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {entries.map((entry) => {
                        const Icon = iconFor(entry.type);
                        return (
                            <li key={entry.id} className="flex gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-[var(--color-bg-hover)] flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Icon className="w-3.5 h-3.5 text-[var(--color-text-secondary)]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-[var(--color-text)] truncate">
                                            {entry.subject || entry.typeLabel}
                                        </span>
                                        <span className="flex items-center gap-1.5 flex-shrink-0">
                                            <span className="text-xs text-[var(--color-text-muted)]">{formatDateTime(entry.createdOn)}</span>
                                            {entry.type === 'note' && (
                                                <button
                                                    type="button"
                                                    onClick={() => { if (confirm('Supprimer cette note ?')) deleteNote.mutate(entry.id); }}
                                                    disabled={deleteNote.isPending}
                                                    className="p-1 rounded hover:bg-[var(--color-error-light)]"
                                                    title="Supprimer la note"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    {entry.content && (
                                        <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap break-words">{entry.content}</p>
                                    )}
                                    {entry.authorName && (
                                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{entry.authorName}</p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
